import { useQuery } from '@tanstack/react-query';
import numbro from 'numbro';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleUp, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { uiActions } from '../../actions';
import fetchStockExpertTips from '../../actions/tips/fetchStockExpertTips';
import Binance from '../../assets/exchange/binance.svg';
import Bybit from '../../assets/exchange/bybit.svg';
import FavStockButtonList from '../../components/FavStockButtonList';
import StockCodeInput from '../../components/form/StockCodeInput';
import HeadOverride from '../../components/general/HeadOverride';
import LoadingImage from '../../components/general/LoadingImage';
import RegistrationPopup from '../../components/general/RegistrationPopup';
import LikeButton from '../../components/like/LikeButton';
import LikeOverlay from '../../components/like/LikeOverlay';
import PriceTimeBadge from '../../components/price-viewer/PriceTimeBadge';
import PriceViewer from '../../components/price-viewer/PriceViewer';
import StockShortkeys from '../../components/shortkey/StockShortkeys';
import { categoryToRegin } from '../../components/stock/stockCategory';
import StockNews from '../../components/stock/StockNews';
import StockProfile from '../../components/stock/StockProfile';
import { uiHelpers } from '../../helpers';
import {
  useAnalytics,
  usePageVisitAnalytics,
} from '../../helpers/analytics.helper';
import useDispatch from '../../hooks/useDispatch';
import { useGeneralErrorHandler } from '../../hooks/useGeneralErrorHandler';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useMembership, useUser } from '../../hooks/users';
import useSelector from '../../hooks/useSelector';
import { FavStock } from '../../py-generated/datalouder/favorite_stock/types';
import { LatestQuote } from '../../py-generated/datalouder/price/types';
import { dailypriceService, stockService } from '../../services';
import { isStockLookupRedirectResponse } from '../../services/stock.service';
import { Analytics } from '../../shared/types';
import PurchaseVIPFloatButton from '../FreeUser/PurchaseVIPFloatButton';
import TutorialQuestionMark from '../Help/TutorialQuestionMark';
import IndexBar from '../Ranking/IndexBar';
import StockPageStatisticsTable from './StockPageStatisticsTable';

export const quickPriceChangeSpan = (
  current: number,
  previous: number
): ReactElement => {
  return (
    <span>
      <span style={{ marginLeft: '8px' }}>
        {uiHelpers.formatDeltaMoney(current - previous)}
      </span>
      <span
        style={{
          color: uiHelpers.colorForChange(
            current === 0 ? 0 : (current - previous) / previous
          ),
          marginLeft: '8px',
        }}
      >
        (
        {numbro(current === 0 ? 0 : (current - previous) / previous).format(
          '+0.00%'
        )}
        )
      </span>
    </span>
  );
};

export const quickPriceChangeSpanV2 = (
  current: number,
  previous: number
): ReactElement => {
  return (
    <span
      style={{
        color: uiHelpers.colorForChange(
          current === 0 ? 0 : (current - previous) / previous
        ),
      }}
    >
      <span style={{ marginLeft: '0.25rem' }}>
        {uiHelpers
          .formatDeltaMoney(current - previous)
          .toString()
          .replace('-', '')
          .replace('+', '')}
      </span>
      <span style={{ marginLeft: '0.25rem' }}>
        (
        {numbro(current === 0 ? 0 : (current - previous) / previous)
          .format('+0.00%')
          .toString()
          .replace('-', '')
          .replace('+', '')}
        )
      </span>
    </span>
  );
};
const StockPage: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const is500 = useMediaQuery('(max-width: 500px)');
  usePageVisitAnalytics();
  const dispatch = useDispatch();
  const { stockCode: paramStockCode } = useParams<'stockCode'>();

  const { stockCode } = useSelector(state => state.ui.stockPage);
  const [searchParams] = useSearchParams();
  const categoryInUrl = searchParams.get('category');
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [realtimePrice, setRealtimePrice] = useState<LatestQuote>();
  const [showPriceViewerPreferences, setShowPriceViewerPreferences] =
    useState(true);
  const dispatchWithEvent = useCallback(
    (callbackName, useParameterAsValue = false) => {
      return e => {
        dispatch(
          uiActions.stockPage[callbackName](
            useParameterAsValue ? e : e.target.value
          )
        );
      };
    },
    [dispatch]
  );

  const [queue] = useAnalytics();

  const user = useUser();

  const {
    isFetching: lookingUp,
    data: lookupResult,
    refetch: lookup,
    error: lookupError,
  } = useQuery(
    ['lookup', paramStockCode, categoryInUrl],
    () => stockService.lookup(paramStockCode, categoryInUrl),
    { enabled: false }
  );

  useGeneralErrorHandler(lookupError);

  const search = useCallback(
    (stockCode: string) => {
      lookup();
      queue(Analytics.ActionEnum.Lookup, stockCode);
    },
    [lookup, queue]
  );

  const navigate = useNavigate();
  const navToStock = useCallback(
    (stockCode: string, category_str?: string) => {
      if (category_str) {
        navigate(`/lookup/${stockCode}?category=${category_str}`);
      } else navigate(`/lookup/${stockCode}`);
    },
    [navigate]
  );

  const handleSearch = useCallback(
    (e, _stockCode?: string) => {
      e.preventDefault();
      if (!stockCode && !_stockCode) return;
      let theStockCode = _stockCode
        ? _stockCode.toLowerCase()
        : stockCode.toLowerCase();
      if (new RegExp('^\\d+$').test(theStockCode)) {
        theStockCode = theStockCode.toString().padStart(5, '0');
      }
      navToStock(theStockCode);
    },
    [navToStock, stockCode]
  );

  const handleComparisonModal = useCallback((show: boolean) => {
    setShowComparisonModal(show);
  }, []);

  const handleShowNewsAnalytics = useCallback(
    news => {
      queue(Analytics.ActionEnum.ViewNewsFile, stockCode, news);
    },
    [queue, stockCode]
  );

  useEffect(() => {
    if (paramStockCode) {
      dispatchWithEvent('stockCodeChanged')({
        target: { value: paramStockCode },
      });
      search(paramStockCode);
    }
  }, [search, paramStockCode, dispatchWithEvent]);

  useEffect(() => {
    // redirect
    if (lookupResult && isStockLookupRedirectResponse(lookupResult)) {
      navToStock(lookupResult.ticker);
    }
  }, [lookupResult, navToStock]);

  useEffect(() => {
    if (lookupResult && !isStockLookupRedirectResponse(lookupResult) && user) {
      // only registered users can make requests to list tips.
      dispatch(fetchStockExpertTips(lookupResult.stock.stockCode));
    }
  }, [dispatch, lookupResult, user]);

  const { stock } = (() => {
    if (lookupResult && !isStockLookupRedirectResponse(lookupResult)) {
      return lookupResult;
    }
    return { stock: undefined };
  })();

  const fetchRealtimePrice = useCallback(async () => {
    if (stock) {
      const stockId = stock.category + ':' + stock.stockCode;
      const rp = await dailypriceService.realtimePrice([stockId]);
      if (rp) {
        setRealtimePrice(rp[0]);
      }
    }
  }, [stock]);

  useEffect(() => {
    if (stock) {
      fetchRealtimePrice();
    }
  }, [fetchRealtimePrice, stock]);
  const { t, i18n } = useTranslation();

  const hasMembership = useMembership();
  const hidePrice = !hasMembership && stock?.stockCode === '^IXIC';

  return (
    <Container>
      <HeadOverride>Lookup</HeadOverride>
      <IndexBar />
      {stock && <PurchaseVIPFloatButton />}
      <RegistrationPopup />
      <Card>
        <Card.Header>
          {t('Lookup')} <TutorialQuestionMark tutorialId={31} alwaysVisible />
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="justify-content-md-center">
              <Form.Group
                className="form-group"
                as={Col}
                md="auto"
                onFocus={e => {
                  (e.target as HTMLInputElement).select();
                }}
                onKeyUp={e => {
                  e.key === 'Enter' && (e.target as HTMLInputElement).select();
                }}
              >
                <StockCodeInput
                  value={stockCode}
                  onChange={dispatchWithEvent('stockCodeChanged', true)}
                  onKeyPress={e => {
                    stockCode !== '' && e.key === 'Enter' && handleSearch(e);
                  }}
                  onSelected={navToStock}
                />
              </Form.Group>
              <Form.Group className="form-group" as={Col} md="auto">
                <Form.Control
                  className="search-button"
                  disabled={stockCode === ''}
                  as={Button}
                  onClick={handleSearch}
                >
                  {t('Search')}
                </Form.Control>
              </Form.Group>
            </Row>
          </Form>
          <Row>
            <Col className="text-center">
              <FavStockButtonList
                onSelect={(_, stock: FavStock) => {
                  navToStock(stock.ticker, stock.category);
                }}
              />
            </Col>
          </Row>
          <LoadingImage displayAsBlock={true} loading={lookingUp} />
          {paramStockCode && !lookingUp && !stock && (
            <p className="text-center">{t('stock-not-found')}</p>
          )}
        </Card.Body>
      </Card>
      <CSSTransition
        in={!!stock}
        timeout={1000}
        classNames="animated-fade"
        unmountOnExit
      >
        <div>
          {stock && (
            <Card>
              <Card.Header
                style={{
                  padding: isMobile ? '0 20px' : '0 5%',
                  marginBottom: 15,
                  display: isMobile ? '' : 'flex',
                }}
              >
                <LikeOverlay stockCode={stock.stockCode}>
                  <span>
                    <span
                      style={{ fontWeight: 'bold', fontSize: is500 ? 20 : 16 }}
                    >
                      {stock.stockCode}
                    </span>{' '}
                    <span style={{ fontWeight: 'bold' }}>
                      {stock.category === 'US' // why we have this logic?
                        ? uiHelpers.tradChify(stock[`name_${i18n.language}`])
                        : stock[`name_${i18n.language}`]}{' '}
                      {
                        <Badge
                          className={`badge-` + stock.category.toLowerCase()}
                          bg="primary"
                        >
                          {t('stock-type-' + stock.category.toLowerCase())}
                        </Badge>
                      }
                      {stock.category === 'CRPTBN' ? <img src={Binance} /> : ''}
                      {stock.category === 'CRPT' ? <img src={Bybit} /> : ''}
                    </span>{' '}
                    &nbsp;
                  </span>
                </LikeOverlay>
                <LikeButton
                  category={stock.category}
                  ticker={stock.stockCode}
                  showBothButtons
                />
                <span
                  style={{
                    display: hidePrice ? 'none' : is500 ? 'block' : undefined,
                    marginLeft: is500 ? 0 : 8,
                  }}
                >
                  {realtimePrice && (
                    <span style={{ fontWeight: 'bold' }}>
                      {uiHelpers.formatMoney(realtimePrice.c)}
                    </span>
                  )}
                  {realtimePrice &&
                    quickPriceChangeSpan(realtimePrice.c, realtimePrice.p_c)}
                  {realtimePrice && <PriceTimeBadge ts={realtimePrice.ts} />}
                </span>
                <div style={{ display: 'flex' }}>
                  <span
                    style={{
                      marginLeft: is500 ? 0 : 8,
                    }}
                  >
                    <Button
                      variant="primary"
                      onClick={() => {
                        return handleComparisonModal(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        color: '#000',
                        border: '2px solid #000',
                        borderRadius: '5px',
                        fontSize: '14px',
                        padding: '2px 8px',
                      }}
                    >
                      <FaPlus style={{ marginRight: '2px', color: 'green' }} />
                      {t('comparisonStock')}
                    </Button>
                  </span>
                  {isMobile && (
                    <span
                      style={{
                        border: '2px solid rgb(0, 0, 0)',
                        padding: '2px 8px',
                        borderRadius: '5px',
                        fontSize: '14px',
                        marginLeft: '8px',
                      }}
                    >
                      {showPriceViewerPreferences ? (
                        <FaAngleUp
                          onClick={() => {
                            setShowPriceViewerPreferences(false);
                          }}
                        />
                      ) : (
                        <FaAngleDown
                          onClick={() => {
                            setShowPriceViewerPreferences(true);
                          }}
                        />
                      )}
                    </span>
                  )}
                </div>
              </Card.Header>
              <Card.Body style={{ padding: '0' }}>
                <PriceViewer
                  stockCode={stock.stockCode}
                  category={stock.category}
                  regionOverride={categoryToRegin(stock.category)}
                  scrollToChartWhenComplete
                  noLittleSearchField
                  isCryptoStock={stock.category.startsWith('CRPT')}
                  handleComparisonModal={handleComparisonModal}
                  showComparisonModal={showComparisonModal}
                  showPriceViewerPreferences={showPriceViewerPreferences}
                />
              </Card.Body>
            </Card>
          )}
          {stock && (
            <Card className="statistics-table">
              <Card.Header>
                {t('statistics')}{' '}
                <TutorialQuestionMark tutorialId={32} alwaysVisible />
              </Card.Header>
              <Card.Body>
                <StockPageStatisticsTable stock={stock} />
              </Card.Body>
            </Card>
          )}
          {stock && (
            <StockShortkeys
              ticker={stock.stockCode}
              category={stock.category}
            />
          )}
        </div>
      </CSSTransition>
      <CSSTransition
        in={!!stock}
        timeout={1000}
        classNames="animated-fade"
        unmountOnExit
      >
        <div>
          {stock && (
            <Card className="company-intro">
              <Card.Header>{t('company-intro')}</Card.Header>
              <Card.Body>
                <StockProfile
                  ticker={stock.stockCode}
                  category={stock.category}
                  language={i18n.language}
                  key={`profile-${stock.stockCode}-${i18n.language}`}
                />
              </Card.Body>
            </Card>
          )}
        </div>
      </CSSTransition>
      <CSSTransition
        in={!!stock}
        timeout={1000}
        classNames="animated-fade"
        unmountOnExit
      >
        <div>
          {stock?.category === 'HK' && (
            <Card className="company-news">
              <Card.Header>{t('company-news')}</Card.Header>
              <Card.Body>
                <StockNews
                  stockCode={stock.stockCode}
                  language={i18n.language}
                  key={`news-${stock.stockCode}-${i18n.language}`}
                  onClickNews={handleShowNewsAnalytics}
                  useHyperlink
                />
              </Card.Body>
            </Card>
          )}
        </div>
      </CSSTransition>
    </Container>
  );
};

export default StockPage;
